import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { useFeature } from 'flagged';
import camelCase from 'lodash/camelCase';
import PropTypes from 'prop-types';
import { ShowLessArrowsIcon, ShowMoreArrowsIcon } from '@/assets/images';
import CustomLink from '@/components/common/CustomLink';
import ControlledCheckbox from '@/components/common/form/ControlledCheckbox';
import ControlledDateInput from '@/components/common/form/ControlledDateInput';
import ControlledTextField from '@/components/common/form/ControlledTextField';
import FeatureFlags from '@/constants/system/FeatureFlags';
import translate from '@/lib/intl/translate';
import { ConstituentType } from '@/lib/types/ConstituentPropType';
import { formatUrl, URL_REGEXP } from '@/utils/urls';
import ConstituentField from '../constants/ConstituentField';
import TemporaryField from '../constants/TemporaryField';

const URL_FIELDS = [
  'WEBSITE',
  'INSTAGRAM',
  'TWITTER',
  'LINKEDIN',
  'FACEBOOK',
  'YOUTUBE',
  'TIKTOK',
];
const DOB = 'dob';
const urlFieldNames = URL_FIELDS.map((field) => ConstituentField[field]);
const isHashForInnerField = (hash) =>
  hash && [TemporaryField.BIRTHDAY, ...urlFieldNames].includes(hash.slice(1));

function AdditionalFields({ lockedAttributes }) {
  const { hash } = useLocation();
  // If the hash matches one of the fields in this component, we need to show
  // these additional fields on initial render.
  const [showMore, setShowMore] = React.useState(isHashForInnerField(hash));
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();
  const constituentType = watch(ConstituentField.CONSTITUENT_TYPE);
  const hasIsDeceased = useFeature(FeatureFlags.IS_DECEASED);

  const watchedURLValues = watch(urlFieldNames);
  React.useEffect(() => {
    urlFieldNames.map((field, i) =>
      setValue(field, formatUrl(watchedURLValues[i])),
    );
  }, watchedURLValues);

  return (
    <>
      {showMore && (
        <>
          {constituentType === ConstituentType.PRS && !hasIsDeceased && (
            <ControlledDateInput
              classes={{ root: 'lg:col-span-2' }}
              control={control}
              disabled={lockedAttributes.has(DOB)}
              id={`cons-${TemporaryField.BIRTHDAY}`}
              label={translate('BIRTHDAY_LABEL')}
              name={TemporaryField.BIRTHDAY}
            />
          )}
          {constituentType === ConstituentType.PRS && hasIsDeceased && (
            <>
              <ControlledDateInput
                classes={{
                  root: 'lg:col-span-3',
                }}
                control={control}
                disabled={lockedAttributes.has(DOB)}
                id={`cons-${TemporaryField.BIRTHDAY}`}
                label={translate('BIRTHDAY_LABEL')}
                name={TemporaryField.BIRTHDAY}
              />
              <ControlledCheckbox
                control={control}
                disabled={lockedAttributes.has(
                  camelCase(ConstituentField.IS_DECEASED),
                )}
                label={translate('IS_DECEASED_LABEL')}
                name={ConstituentField.IS_DECEASED}
              />
            </>
          )}

          {URL_FIELDS.map((field) => (
            <ControlledTextField
              className="lg:col-span-2"
              control={control}
              disabled={lockedAttributes.has(field?.toLowerCase?.())}
              error={!!errors[ConstituentField[field]]}
              helperText={errors[ConstituentField[field]]?.message}
              key={field}
              label={translate(`${field}_LABEL`)}
              name={ConstituentField[field]}
              rules={{
                pattern: {
                  value: URL_REGEXP,
                  message: translate('INVALID_URL_FORMAT_MESSAGE'),
                },
              }}
            />
          ))}
        </>
      )}
      <div className="col-span-full">
        <CustomLink
          component="button"
          onClick={() => setShowMore(!showMore)}
          className="flex items-center"
          type="button"
        >
          <span className="mr-2">
            {translate(
              showMore ? 'SHOW_LESS_OPTIONS_LABEL' : 'ADDITIONAL_OPTIONS',
            )}
          </span>
          {showMore ? <ShowLessArrowsIcon /> : <ShowMoreArrowsIcon />}
        </CustomLink>
      </div>
    </>
  );
}

AdditionalFields.propTypes = {
  lockedAttributes: PropTypes.instanceOf(Set).isRequired,
};

export default AdditionalFields;
